import React, { useEffect, useState, useMemo } from "react";
import api from "../../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Input } from "../../components/ui/input";
import { Users, ShoppingBag, Store, Search } from "lucide-react";

function RoleChip({ role }) {
  const map = {
    super_admin: { label: "Super Admin", cls: "bg-[#7A1C2B]/20 text-[#7A1C2B] border border-[#7A1C2B]/40" },
    admin_broker: { label: "Broker", cls: "bg-[#2a1a05]/20 text-[#C9A961] border border-[#C9A961]/40" },
    buyer: { label: "Rivenditore B2B", cls: "bg-emerald-950/30 text-emerald-400 border border-emerald-700/40" },
    b2c_customer: { label: "Cliente B2C", cls: "bg-blue-950/30 text-blue-400 border border-blue-700/40" },
  };
  const v = map[role] || { label: role, cls: "bg-muted text-foreground" };
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${v.cls}`}>{v.label}</span>;
}

function UsersTable({ rows, emptyText }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((u) =>
      [u.name, u.email, u.company, u.country, u.vat_number]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(term))
    );
  }, [rows, q]);

  return (
    <div className="space-y-3">
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          data-testid="customers-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca per nome, email, azienda, P.IVA..."
          className="pl-9"
        />
      </div>
      <div className="bg-card border border-border rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead><TableHead>Email</TableHead><TableHead>Ruolo</TableHead>
              <TableHead>Azienda</TableHead><TableHead>P.IVA</TableHead>
              <TableHead>Paese</TableHead><TableHead>Verificato</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">{emptyText}</TableCell></TableRow>
            ) : filtered.map((u) => (
              <TableRow key={u.id} data-testid={`customer-row-${u.id}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-sm">{u.email}</TableCell>
                <TableCell><RoleChip role={u.role} /></TableCell>
                <TableCell className="text-sm">{u.company || "—"}</TableCell>
                <TableCell className="text-xs font-mono">{u.vat_number || "—"}</TableCell>
                <TableCell>{u.country || "—"}</TableCell>
                <TableCell>{u.verified ? <span className="text-green-500">✓</span> : <span className="text-muted-foreground">—</span>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="text-xs text-muted-foreground">{filtered.length} {filtered.length === 1 ? "risultato" : "risultati"}</div>
    </div>
  );
}

export default function AdminCustomers() {
  const [users, setUsers] = useState([]);

  useEffect(() => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  const b2c = users.filter((u) => u.role === "b2c_customer");
  const b2b = users.filter((u) => u.role === "buyer");
  const all = [...b2b, ...b2c];

  const counts = {
    all: all.length,
    b2c: b2c.length,
    b2b: b2b.length,
  };

  return (
    <div data-testid="admin-customers-page" className="container-xl py-8">
      <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--accent))] mb-2">Super Admin · Clienti</div>
      <h1 className="editorial-heading text-4xl">Clienti</h1>
      <p className="text-muted-foreground mt-2 mb-6">Lista separata di clienti B2C e rivenditori B2B. I broker hanno una sezione dedicata.</p>

      <Tabs defaultValue="all" className="mt-6">
        <TabsList>
          <TabsTrigger data-testid="customers-tab-all" value="all" className="gap-2">
            <Users className="h-4 w-4" /> Tutti <span className="ml-1 text-xs opacity-60">({counts.all})</span>
          </TabsTrigger>
          <TabsTrigger data-testid="customers-tab-b2b" value="b2b" className="gap-2">
            <Store className="h-4 w-4" /> Rivenditori B2B <span className="ml-1 text-xs opacity-60">({counts.b2b})</span>
          </TabsTrigger>
          <TabsTrigger data-testid="customers-tab-b2c" value="b2c" className="gap-2">
            <ShoppingBag className="h-4 w-4" /> Clienti B2C <span className="ml-1 text-xs opacity-60">({counts.b2c})</span>
          </TabsTrigger>
        </TabsList>
        <TabsContent value="all" className="mt-6"><UsersTable rows={all} emptyText="Nessun cliente registrato." /></TabsContent>
        <TabsContent value="b2b" className="mt-6"><UsersTable rows={b2b} emptyText="Nessun rivenditore B2B registrato." /></TabsContent>
        <TabsContent value="b2c" className="mt-6"><UsersTable rows={b2c} emptyText="Nessun cliente B2C registrato." /></TabsContent>
      </Tabs>
    </div>
  );
}
